import React, { useState, useEffect, useRef } from 'react';
import EdgeOrREST from '../EdgeOrREST';
import {
    Box,
    Typography,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Button,
    Checkbox,
} from '@mui/material';

export default function TemplateEditor({
    selectedTemplate = {},
    reportElements = [],
    onChange,
    onClose,
    handleSaveTemplate,
}) {
    const [template, setTemplate] = useState(selectedTemplate);
    const [enabledMap, setEnabledMap] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [groups, setGroups] = useState({});
    const [groupEnabledMap, setGroupEnabledMap] = useState({});
    const tableRef = useRef(null);

    useEffect(() => {
        setTemplate(selectedTemplate);
    }, [selectedTemplate]);

    const handleChange = (field, value) => {
        setTemplate(prev => ({ ...prev, [field]: value }));
        onChange?.({ ...template, [field]: value });
    };

    const normaliseSegment = (segRaw) => {
        if (!segRaw) return '';
        const seg = String(segRaw).toLowerCase();
        if (/^p\d+$/i.test(seg)) return 'page';
        if (/^page\d+$/i.test(seg)) return 'page';
        return seg.replace(/\d+$/, '');
    };

    const normaliseId = (idString) => {
        if (!idString) return [];
        const rawSegments = String(idString).split(/[-_\s]+/);
        const norm = rawSegments
            .map(s => normaliseSegment(s))
            .filter(Boolean);
        return norm;
    };

    const shareTwoSegments = (a, b) => {
        if (!a.length || !b.length) return false;
        const setA = new Set(a);
        let count = 0;
        for (const s of b) {
            if (setA.has(s)) {
                count++;
                if (count >= 2) return true;
            }
        }
        return false;
    };

    class UnionFind {
        constructor(n) {
            this.parent = new Array(n).fill(0).map((_, i) => i);
        }
        find(x) {
            if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);
            return this.parent[x];
        }
        union(a, b) {
            const ra = this.find(a);
            const rb = this.find(b);
            if (ra !== rb) this.parent[rb] = ra;
        }
    }

    const groupNameFromSegments = (segmentsArray) => {
        const segSet = new Set(segmentsArray);

        if (segSet.has('page') && segSet.has('title')) return 'Page Title';
        if (segSet.has('page') && segSet.has('labels')) return 'Page Labels';
        if (segSet.has('company') && segSet.has('logo')) return 'Company Logo';
        if (segSet.has('report') && segSet.has('date')) return 'Report Date';
        if (segSet.has('designer') && segSet.has('name')) return 'Designer Name';
        if (segSet.has('project') && segSet.has('no')) return 'Project Number';
        if (segSet.has('project') && segSet.has('name')) return 'Project Name';
        if (segSet.has('page') && segSet.has('layout')) return 'Page Layout';
        if (segSet.has('luminaire') && segSet.has('repeater')) return 'Luminaire Repeaters';

        const freqs = {};
        for (const s of segmentsArray) freqs[s] = (freqs[s] || 0) + 1;
        const ordered = Object.entries(freqs).sort((a, b) => b[1] - a[1]).map(x => x[0]);

        if (ordered.length >= 2) {
            return ordered.slice(0, 2).map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
        }
        if (ordered.length === 1) {
            const s = ordered[0];
            return s[0].toUpperCase() + s.slice(1);
        }
        return 'Misc';
    };

    const buildGroupsDict = (elements) => {
        const n = elements.length;
        if (n === 0) return {};

        const norms = elements.map(el => normaliseId(el.dynamicElementId || ''));

        const uf = new UnionFind(n);
        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                if (shareTwoSegments(norms[i], norms[j])) {
                    uf.union(i, j);
                }
            }
        }

        const buckets = {};
        for (let i = 0; i < n; i++) {
            const root = uf.find(i);
            if (!buckets[root]) buckets[root] = [];
            buckets[root].push(i);
        }

        const dict = {};
        Object.values(buckets).forEach((idxList) => {
            const allSegments = [];
            idxList.forEach(idx => allSegments.push(...norms[idx]));
            const friendly = groupNameFromSegments(allSegments);

            let name = friendly;
            let suffix = 1;
            while (dict[name]) {
                suffix++;
                name = `${friendly} (${suffix})`;
            }

            dict[name] = idxList.map(idx => ({
                id: elements[idx].id,
                element: elements[idx]
            }));
        });

        return dict;
    };

    const computeGroupEnabledMap = (groups, enabledMap) => {
        const map = {};
        Object.entries(groups).forEach(([groupName, items]) => {
            map[groupName] = items.every(item => enabledMap[item.id]);
        });
        return map;
    };

    useEffect(() => {
        setGroupEnabledMap(computeGroupEnabledMap(groups, enabledMap));
    }, [groups, enabledMap]);

    const toggleGroup = (groupName) => {
        setEnabledMap(prev => {
            const newMap = { ...prev };
            const items = groups[groupName];

            const newValue = !items.every(item => prev[item.id]);

            items.forEach(item => {
                newMap[item.id] = newValue;
            });
            return newMap;
        });
    };

    useEffect(() => {
        if (reportElements && reportElements.length) {
            const initial = {};
            let enabledIds = [];
            if (Array.isArray(selectedTemplate?.elements)) {
                enabledIds = selectedTemplate.elements.map((n) => Number(n));
            }
            reportElements.forEach((el) => {
                initial[el.id] = enabledIds.includes(Number(el.id));
            });
            setEnabledMap(initial);

            const groupedDict = buildGroupsDict(reportElements);
            setGroups(groupedDict);
            console.log("Template Editor Groups (dict):", groupedDict);
        } else {
            setEnabledMap({});
            setGroups({});
        }
    }, [reportElements, selectedTemplate]);

    const allSelected = Object.values(enabledMap).every(v => v === true);
    const handleSelectAllToggle = () => {
        const newValue = !allSelected;
        const updated = {};
        Object.keys(enabledMap).forEach(id => {
            updated[id] = newValue;
        });
        setEnabledMap(updated);
    };

    const filteredGroups = Object.entries(groups)
        .map(([groupName, items]) => {
            const match = items.some(item =>
                item.element.dynamicElementId
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );

            return { groupName, items, match };
        })
        .sort((a, b) => Number(b.match) - Number(a.match));

    useEffect(() => {
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [searchTerm]);

    return (
        <>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Template Editor
                </Typography>

                {selectedTemplate?.id ? (
                    <>
                        {/* --- Template Metadata --- */}
                        <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 3 }}>
                            <TextField
                                label="Template Name"
                                value={template.templateName || ''}
                                onChange={(e) => handleChange('templateName', e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Description"
                                value={template.description || ''}
                                onChange={(e) => handleChange('description', e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                            />
                        </Box>

                        {/* --- Elements Header Row --- */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 2 }}>
                            <Typography variant="subtitle1">Elements</Typography>
                            <Box display="flex" gap={1}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder="Search elements..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleSelectAllToggle}
                                >
                                    {allSelected ? "Deselect All" : "Select All"}
                                </Button>
                            </Box>
                        </Box>

                        {/* --- Table --- */}
                        <TableContainer
                            ref={tableRef} // NEW
                            component={Paper}
                            variant="outlined"
                            sx={{ mb: 3, maxHeight: 300 }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Element</TableCell>
                                        <TableCell>Is Image?</TableCell>
                                        <TableCell>Enabled?</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredGroups.length > 0 ? (
                                        filteredGroups.map(({ groupName, items }) => (
                                            <TableRow key={groupName}>
                                                <TableCell>
                                                    <strong>{groupName}</strong>
                                                </TableCell>

                                                <TableCell>
                                                    {/* show image icon if ANY element in group is image */}
                                                    {items.some(i => i.element.isImage != null) ? "Yes" : "—"}
                                                </TableCell>

                                                <TableCell>
                                                    <Checkbox
                                                        checked={groupEnabledMap[groupName] ?? false}
                                                        onChange={() => toggleGroup(groupName)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No elements found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* --- Bottom Buttons --- */}
                        <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, pr: 1, gap: 2 }}>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => handleSaveTemplate({
                                    ...selectedTemplate,
                                    elements: enabledMap,
                                    description: template.description,
                                    templateName: template.templateName,
                                    isOverwrite: true,
                                })}
                            >
                                Overwrite Existing
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleSaveTemplate({
                                    ...selectedTemplate,
                                    elements: enabledMap,
                                    description: template.description,
                                    templateName: template.templateName,
                                })}
                            >
                                Save as New
                            </Button>
                            <Button
                                variant="contained"
                                color="inherit"
                                onClick={onClose}
                            >
                                Close
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography color="text.secondary">No template selected.</Typography>
                )}
            </Paper>
        </>
    );
}
